import { isDriveKind, itemPath, type Game } from '@hive/shared';
import type { HiveFileRow, VaultLinkRow } from '@/data/vault';
import { driveThumbnail } from '@/lib/drive';

// Small pure helpers for Vault's screens.

export type Entry = { type: 'vault_link'; row: VaultLinkRow } | { type: 'file'; row: HiveFileRow };

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/** What a link shows as: its title, or its URL when it has none. */
export function linkTitle(link: Pick<VaultLinkRow, 'title' | 'url'>): string {
  return link.title.trim() || link.url;
}

/** A Drive API thumbnailLink at a useful size (they come at 220 px). */
export function sizedThumbnail(thumbnailLink: string, width = 640): string {
  return /=s\d+$/.test(thumbnailLink) ? thumbnailLink.replace(/=s\d+$/, `=s${width}`) : thumbnailLink;
}

/**
 * Thumbnail URLs for a link, best first. For Drive files that's the Drive
 * API's thumbnailLink (works anywhere, if Hive can see the file), then, in a
 * browser signed in to Google, Drive's own thumbnail URL.
 */
export function linkThumbnails(
  link: Pick<VaultLinkRow, 'kind' | 'provider_id' | 'thumbnail_url'>,
  options: { driveThumbnail?: string | null; inBrowser: boolean },
): string[] {
  if (isDriveKind(link.kind) && link.kind !== 'drive_folder' && link.provider_id) {
    return [
      options.driveThumbnail ? sizedThumbnail(options.driveThumbnail) : null,
      options.inBrowser ? driveThumbnail(link.provider_id) : null,
    ].filter((s): s is string => !!s);
  }
  return link.thumbnail_url ? [link.thumbnail_url] : [];
}

/** Where an item opens, given the games (tasks and games need a slug). */
export function pathFor(
  item: { type: string; id: string; game_id: string | null; parent_id: string | null },
  games: readonly Game[],
): string {
  const gameId = item.type === 'game' ? item.id : item.game_id;
  return itemPath(item, { gameSlug: games.find((g) => g.id === gameId)?.slug ?? null });
}

/** Filters an entry list by game ('' = all, 'studio' = studio-wide) and kind ('' = all, 'file' = Hive files). */
export function filterEntries(entries: readonly Entry[], game: string, kind: string, studioId: string | null): Entry[] {
  return entries.filter((e) => {
    const gameId = e.row.game_id;
    if (game === 'studio' && gameId !== null && gameId !== studioId) return false;
    if (game && game !== 'studio' && gameId !== game) return false;
    if (kind === 'file') return e.type === 'file';
    if (kind) return e.type === 'vault_link' && e.row.kind === kind;
    return true;
  });
}
